import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Tabs, Tab, Badge, Alert, Table, Spinner, Button, Form, ProgressBar, ListGroup } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import ExternalNavbar from '../components/ExternalNavbar';
import ProgressReportGenerator from '../components/ProgressReportGenerator';
import facilitatorService from '../services/facilitatorService';
import AttendanceRegister from '../components/AttendanceRegister';
import GoogleMeetLinks from '../components/GoogleMeetLinks';
import '../styles/brand.css';

const ExternalFacilitatorDashboard = () => {
  const { currentUser, userProfile, isExternalFacilitator } = useAuth();
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [assignedCohorts, setAssignedCohorts] = useState([]);
  const [learners, setLearners] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [loginFilter, setLoginFilter] = useState('all'); // all, logged_in, never_logged_in
  const [attendanceFilter, setAttendanceFilter] = useState('all'); // all, absent, present
  const [searchTerm, setSearchTerm] = useState('');
  const [commentType, setCommentType] = useState('daily');
  const [commentCohortId, setCommentCohortId] = useState('');
  const [commentDate, setCommentDate] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const [dailyComment, setDailyComment] = useState('');
  const [weeklyHighs, setWeeklyHighs] = useState('');
  const [weeklyLows, setWeeklyLows] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState('');
  const [commentError, setCommentError] = useState('');
  const [commentHistory, setCommentHistory] = useState([]);
  const [commentHistoryLoading, setCommentHistoryLoading] = useState(false);

  // Load data effect (must be before any conditional returns)
  useEffect(() => {
    if (userProfile) {
      loadData();
    }
  }, [userProfile]);

  // Access guard (after hooks)
  if (!isExternalFacilitator || !isExternalFacilitator()) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">
          <strong>Access Denied</strong>
          <p className="mb-0">This page is for external facilitators only.</p>
        </Alert>
      </div>
    );
  }

  const hydrateLearnerDocuments = async (learnerList) => {
    if (!Array.isArray(learnerList) || learnerList.length === 0) return;
    try {
      const updated = [];
      const chunkSize = 10;
      for (let i = 0; i < learnerList.length; i += chunkSize) {
        const chunk = learnerList.slice(i, i + chunkSize);
        const docsResults = await Promise.all(
          chunk.map((learner) =>
            facilitatorService.getLearnerDocuments(learner.id).catch(() => ({}))
          )
        );
        chunk.forEach((learner, index) => {
          updated.push({ ...learner, documents: docsResults[index] });
        });
      }
      setLearners((prev) => {
        const docMap = new Map(updated.map((learner) => [learner.id, learner.documents]));
        return prev.map((learner) => ({
          ...learner,
          documents: docMap.get(learner.id) || learner.documents
        }));
      });
    } catch (error) {
      devLog('Background document hydration failed:', error);
    }
  };

  const loadData = async () => {
      setLoading(true);
    try {
      const assignedCohortIds = userProfile.assignedCohorts || [];
      const assignedProgramIds = Array.isArray(userProfile.assignedPrograms) ? userProfile.assignedPrograms : [];

      const isActiveLearner = (learner) => {
        if (!learner) return false;
        if (learner.deleted || learner.deactivated) return false;
        if (learner.isActive === false) return false;
        const status = String(learner.status || '').toLowerCase();
        if (['inactive', 'dropoff', 'drop_off', 'drop off', 'dropped'].includes(status)) return false;
        return true;
      };

      const [learnersData, resourcesData, cohortDocs] = await Promise.all([
        facilitatorService.getLearnersByAssignedPrograms(currentUser.uid, { includeDocuments: false }),
        facilitatorService.getResources(currentUser.uid),
        assignedCohortIds.length > 0
          ? getDocs(query(collection(db, 'cohorts'), where('__name__', 'in', assignedCohortIds.slice(0, 10))))
          : assignedProgramIds.length > 0
          ? getDocs(query(collection(db, 'cohorts'), where('programId', 'in', assignedProgramIds.slice(0, 10))))
          : Promise.resolve({ docs: [] })
      ]);

      const cohortMap = new Map(
        cohortDocs.docs.map(doc => [doc.id, { id: doc.id, ...doc.data() }])
      );

      const filteredLearners = learnersData
        .filter(isActiveLearner)
        .filter((learner) => {
          if (assignedCohortIds.length > 0) return assignedCohortIds.includes(learner.cohortId);
          if (assignedProgramIds.length > 0) return assignedProgramIds.includes(learner.programId);
          return false;
        });

      const filteredResources = resourcesData.filter((resource) => {
        const cohortIds = Array.isArray(resource.cohortIds)
          ? resource.cohortIds
          : (resource.cohortId ? [resource.cohortId] : []);
        if (cohortIds.length > 0) {
          return cohortIds.some((id) => assignedCohortIds.includes(id));
        }
        if (resource.programId) {
          return assignedProgramIds.includes(resource.programId);
        }
        return false;
      });

      setLearners(filteredLearners);
      setResources(filteredResources);

      if (assignedProgramIds.length > 0) {
        const programDocs = await getDocs(
          query(collection(db, 'programs'), where('__name__', 'in', assignedProgramIds.slice(0, 10)))
        );
        setAssignedPrograms(programDocs.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } else {
        setAssignedPrograms([]);
      }
      setAssignedCohorts(Array.from(cohortMap.values()));

      const scheduleHydration = window.requestIdleCallback || ((fn) => setTimeout(fn, 250));
      scheduleHydration(() => hydrateLearnerDocuments(filteredLearners));

      const cohorts = assignedCohortIds.length > 0 ? assignedCohortIds : Array.from(cohortMap.keys());

      const allowedCohortIds = [
        ...new Set([...cohorts])
      ];

      // Load attendance records for assigned cohorts or programs
      if (allowedCohortIds.length > 0) {
        try {
          const attendanceRef = collection(db, 'attendance');
          const attendanceQuery = query(
            attendanceRef,
            where('cohortId', 'in', allowedCohortIds.slice(0, 10)),
            orderBy('date', 'desc'),
            limit(100)
          );
          const attendanceSnapshot = await getDocs(attendanceQuery);
          setAttendance(attendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
          devLog('Attendance query failed:', err);
          setAttendance([]);
        }
      } else {
        setAttendance([]);
      }

      await loadCommentHistory();

    } catch (error) {
      devError('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const getStats = () => {
    const loggedInCount = learners.filter(l => l.lastLoginAt || l.hasLoggedIn).length;
    const neverLoggedInCount = learners.filter(l => !l.lastLoginAt && !l.hasLoggedIn).length;
    const activeCount = learners.filter(l => l.isActive !== false).length;

    return {
      totalLearners: learners.length,
      loggedIn: loggedInCount,
      neverLoggedIn: neverLoggedInCount,
      active: activeCount,
      attendanceRecords: attendance.length
    };
  };

  // Filter learners based on login status and search
  const getFilteredLearners = () => {
    let filtered = [...learners];

    // Apply login filter
    if (loginFilter === 'logged_in') {
      filtered = filtered.filter(l => l.lastLoginAt || l.hasLoggedIn);
    } else if (loginFilter === 'never_logged_in') {
      filtered = filtered.filter(l => !l.lastLoginAt && !l.hasLoggedIn);
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(l =>
        (l.displayName || '').toLowerCase().includes(search) ||
        (l.email || '').toLowerCase().includes(search) ||
        (l.idNumber || '').includes(search)
      );
    }

    return filtered;
  };

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Never';
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export learner login report
  const exportLoginReport = () => {
    const data = getFilteredLearners().map(learner => ({
      'Name': learner.displayName || 'N/A',
      'Email': learner.email || 'N/A',
      'ID Number': learner.idNumber || 'N/A',
      'Cohort': assignedCohorts.find(c => c.id === learner.cohortId)?.name || learner.cohortId || 'N/A',
      'Last Login': formatDate(learner.lastLoginAt),
      'Login Status': (learner.lastLoginAt || learner.hasLoggedIn) ? 'Has Logged In' : 'Never Logged In',
      'Status': learner.isActive !== false ? 'Active' : 'Inactive'
    }));

    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `learner-login-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Export attendance report
  const exportAttendanceReport = () => {
    if (attendance.length === 0) {
      alert('No attendance data to export');
      return;
    }

    const data = attendance.map(record => ({
      'Learner': record.learnerName || record.userName || 'N/A',
      'Session': record.sessionName || record.moduleName || 'N/A',
      'Date': formatDate(record.date),
      'Status': record.status || 'N/A',
      'Duration (mins)': record.durationMinutes || 'N/A',
      'Cohort': assignedCohorts.find(c => c.id === record.cohortId)?.name || record.cohortId || 'N/A',
      'Notes': record.notes || record.reason || 'N/A'
    }));

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `attendance-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleSubmitComment = async () => {
    if (!commentCohortId) {
      setCommentError('Please select a cohort.');
      return;
    }

    if (commentType === 'daily') {
      if (!commentDate) {
        setCommentError('Please select a date.');
        return;
      }
      if (!dailyComment.trim()) {
        setCommentError('Please add your daily comment.');
        return;
      }
    }

    if (commentType === 'weekly') {
      if (!weekStartDate || !weekEndDate) {
        setCommentError('Please select the week start and end dates.');
        return;
      }
      if (!weeklyHighs.trim() || !weeklyLows.trim()) {
        setCommentError('Please add both highs and lows.');
        return;
      }
    }

    try {
      setCommentSubmitting(true);
      setCommentError('');
      setCommentSuccess('');

      const cohortName = assignedCohorts.find(c => c.id === commentCohortId)?.name || commentCohortId;

      const payload = {
        type: commentType === 'daily' ? 'daily_comment' : 'weekly_highs_lows',
        cohortId: commentCohortId,
        cohortName,
        date: commentType === 'daily' ? commentDate : null,
        weekStart: commentType === 'weekly' ? weekStartDate : null,
        weekEnd: commentType === 'weekly' ? weekEndDate : null,
        dailyComment: commentType === 'daily' ? dailyComment.trim() : null,
        weeklyHighs: commentType === 'weekly' ? weeklyHighs.trim() : null,
        weeklyLows: commentType === 'weekly' ? weeklyLows.trim() : null,
        createdByName: userProfile?.displayName || userProfile?.email || 'External Facilitator',
        createdByRole: userProfile?.role || 'external_facilitator'
      };

      await facilitatorService.createProgressReport(payload);

      setCommentSuccess('Comment saved successfully.');
      setDailyComment('');
      setWeeklyHighs('');
      setWeeklyLows('');
      await loadCommentHistory();
    } catch (error) {
      devError('Error saving comment:', error);
      setCommentError('Failed to save comment. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const loadCommentHistory = async () => {
    if (!currentUser?.uid) return;
    try {
      setCommentHistoryLoading(true);
      const snapshot = await getDocs(
        query(
          collection(db, 'progressReports'),
          where('facilitatorId', '==', currentUser.uid)
        )
      );
      const items = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((item) => item.type === 'daily_comment' || item.type === 'weekly_highs_lows')
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 10);
      setCommentHistory(items);
    } catch (error) {
      devError('Error loading comment history:', error);
    } finally {
      setCommentHistoryLoading(false);
    }
  };

  // Scheduling ownership is internal-only. External facilitators can confirm/override attendance,
  // but they must not create or edit sessions.

  // Filter attendance by status
  const getFilteredAttendance = () => {
    let filtered = [...attendance];
    if (attendanceFilter === 'absent') {
      filtered = filtered.filter(a => a.status === 'absent' || a.status === 'excused');
    } else if (attendanceFilter === 'present') {
      filtered = filtered.filter(a => a.status === 'present');
    }
    return filtered;
  };

  // Get absentee summary
  const getAbsenteeSummary = () => {
    const absentees = attendance.filter(a => a.status === 'absent' || a.status === 'excused');
    return {
      total: absentees.length,
      withNotes: absentees.filter(a => a.notes || a.reason).length,
      withoutNotes: absentees.filter(a => !a.notes && !a.reason).length
    };
  };

  // Get resource icon
  const getResourceIcon = (category, type) => {
    const icons = {
      documents: '📄',
      videos: '🎥',
      links: '🔗',
      templates: '📝',
      tools: '🛠️',
      assessments: '📋',
      presentations: '📊',
      research: '📚'
    };
    return icons[category] || '📁';
  };

  const stats = getStats();
  const absenteeSummary = getAbsenteeSummary();
  const filteredAttendance = getFilteredAttendance();
  const filteredLearners = getFilteredLearners();

  return (
    <>
      <ExternalNavbar />
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">External Facilitator Dashboard</h1>
              <p className="mission-text mb-4">
                Manage attendance and view learner progress for assigned cohorts
              </p>
              <p className="mb-0">
                Review learner activity, track attendance, and share progress reports with stakeholders.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading dashboard...</p>
          </div>
        )}

        {!loading && (
          <>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-primary mb-1">{stats.totalLearners}</h3>
                <small className="text-muted">Total Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-success mb-1">{stats.loggedIn}</h3>
                <small className="text-muted">Have Logged In</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-danger mb-1">{stats.neverLoggedIn}</h3>
                <small className="text-muted">Never Logged In</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-info mb-1">{assignedPrograms.length}</h3>
                <small className="text-muted">Assigned Programmes</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main Tabs */}
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          {/* Overview Tab */}
          <Tab eventKey="overview" title="Overview">
            <Row>
              {/* Upcoming Teams Meetings */}
              <Col lg={12} className="mb-4">
                <GoogleMeetLinks />
              </Col>
            </Row>

            <Row>
              {/* Assigned Programmes */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <strong>Assigned Programmes</strong>
                  </Card.Header>
                  <Card.Body>
                    {assignedPrograms.length === 0 ? (
                      <Alert variant="warning" className="mb-0">
                        No programmes assigned yet. Contact the administrator.
                      </Alert>
                    ) : (
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>Programme Name</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedPrograms.map(prog => (
                            <tr key={prog.id}>
                              <td>{prog.name || prog.programName || prog.id}</td>
                              <td>
                                <Badge bg={prog.status === 'active' ? 'success' : 'secondary'}>
                                  {prog.status || 'active'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Assigned Cohorts */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <strong>Assigned Cohorts</strong>
                  </Card.Header>
                  <Card.Body>
                    {assignedCohorts.length === 0 ? (
                      <Alert variant="warning" className="mb-0">
                        No cohorts assigned yet. Contact the administrator.
                      </Alert>
                    ) : (
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>Cohort Name</th>
                            <th>Learners</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedCohorts.map(cohort => (
                            <tr key={cohort.id}>
                              <td>{cohort.name || cohort.id}</td>
                              <td>
                                <Badge bg="primary">
                                  {learners.filter(l => l.cohortId === cohort.id).length} learners
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              {/* Login Status Summary */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <strong>Login Status Summary</strong>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Logged In</span>
                        <span>{stats.loggedIn} / {stats.totalLearners}</span>
                      </div>
                      <ProgressBar
                        now={stats.totalLearners > 0 ? (stats.loggedIn / stats.totalLearners) * 100 : 0}
                        variant="success"
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Never Logged In</span>
                        <span>{stats.neverLoggedIn} / {stats.totalLearners}</span>
                      </div>
                      <ProgressBar
                        now={stats.totalLearners > 0 ? (stats.neverLoggedIn / stats.totalLearners) * 100 : 0}
                        variant="danger"
                      />
                    </div>
                    <hr />
                    <p className="text-muted small mb-0">
                      {Math.round((stats.loggedIn / (stats.totalLearners || 1)) * 100)}% of learners have signed in to the platform.
                    </p>
                  </Card.Body>
                </Card>
              </Col>

              {/* Absentee Summary */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <strong>Absentee Summary</strong>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Total Absences Recorded</span>
                        <Badge bg="danger">{absenteeSummary.total}</Badge>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>With Notes/Reason</span>
                        <Badge bg="info">{absenteeSummary.withNotes}</Badge>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Without Notes</span>
                        <Badge bg="warning">{absenteeSummary.withoutNotes}</Badge>
                      </div>
                    </div>
                    <hr />
                    <p className="text-muted small mb-0">
                      View the Attendance tab for detailed absence records and notes.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* Learners Tab with Login Status */}
          <Tab eventKey="learners" title="Learners & Login Status">
            <Card>
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <strong>Learner List</strong>
                  <Badge bg="secondary" className="ms-2">{filteredLearners.length} learners</Badge>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={loginFilter}
                    onChange={(e) => setLoginFilter(e.target.value)}
                  >
                    <option value="all">All Learners</option>
                    <option value="logged_in">Logged In</option>
                    <option value="never_logged_in">Never Logged In</option>
                  </Form.Select>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Search..."
                    style={{ width: '200px' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button size="sm" variant="outline-primary" onClick={exportLoginReport}>
                    Export CSV
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {filteredLearners.length === 0 ? (
                  <Alert variant="warning" className="mb-0">
                    No learners found matching your criteria.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover size="sm">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>ID Number</th>
                          <th>Cohort</th>
                          <th>Last Login</th>
                          <th>Login Status</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLearners.map(learner => (
                          <tr key={learner.id}>
                            <td>{learner.displayName || learner.name || 'N/A'}</td>
                            <td>{learner.email}</td>
                            <td>{learner.idNumber || 'N/A'}</td>
                            <td>{assignedCohorts.find(c => c.id === learner.cohortId)?.name || learner.cohortName || 'Unknown Cohort'}</td>
                            <td>{formatDate(learner.lastLoginAt)}</td>
                            <td>
                              <Badge bg={(learner.lastLoginAt || learner.hasLoggedIn) ? 'success' : 'danger'}>
                                {(learner.lastLoginAt || learner.hasLoggedIn) ? 'Logged In' : 'Never'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={learner.isActive !== false ? 'success' : 'secondary'}>
                                {learner.isActive !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Attendance Tab */}
          <Tab eventKey="attendance" title="Attendance">
            <Card>
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <strong>Attendance Records</strong>
                  <Badge bg="info" className="ms-2">{filteredAttendance.length} records</Badge>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={attendanceFilter}
                    onChange={(e) => setAttendanceFilter(e.target.value)}
                  >
                    <option value="all">All Records</option>
                    <option value="absent">Absent/Excused Only</option>
                    <option value="present">Present Only</option>
                  </Form.Select>
                  <Button size="sm" variant="outline-primary" onClick={exportAttendanceReport}>
                    Export Attendance
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-4">
                  Scheduling is managed internally. External facilitators can view scheduled sessions and confirm/override attendance, but cannot create or edit sessions.
                </Alert>

              <Card className="mt-4">
                <Card.Header>
                  <strong>Confirm & Override Attendance</strong>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted">
                    Select a session, then mark Present/Absent/Excused and submit overrides.
                  </p>
                  <AttendanceRegister facilitatorMode initialViewMode="override" />
                </Card.Body>
              </Card>

              {filteredAttendance.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  <strong>No attendance records found.</strong>
                    <p className="mb-0 mt-2">
                      Attendance records will appear here once sessions have been conducted
                      and attendance has been recorded for your assigned cohorts.
                    </p>
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover size="sm">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Session</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Notes/Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttendance.slice(0, 50).map(record => (
                          <tr key={record.id}>
                            <td>{record.learnerName || record.userName || 'N/A'}</td>
                            <td>{record.sessionName || record.moduleName || 'N/A'}</td>
                            <td>{formatDate(record.date)}</td>
                            <td>
                              <Badge bg={
                                record.status === 'present' ? 'success' :
                                record.status === 'absent' ? 'danger' :
                                record.status === 'excused' ? 'warning' : 'secondary'
                              }>
                                {record.status || 'N/A'}
                              </Badge>
                            </td>
                            <td>{record.durationMinutes ? `${record.durationMinutes} min` : 'N/A'}</td>
                            <td>
                              {(record.status === 'absent' || record.status === 'excused') ? (
                                record.notes || record.reason ? (
                                  <span className="text-muted small">{record.notes || record.reason}</span>
                                ) : (
                                  <Badge bg="warning" className="small">No notes</Badge>
                                )
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {filteredAttendance.length > 50 && (
                      <p className="text-muted text-center">
                        Showing first 50 records. Export to see all {filteredAttendance.length} records.
                      </p>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Resources Tab */}
          <Tab eventKey="resources" title="Resources">
            <Card>
              <Card.Header>
                <strong>📚 Learning Resources</strong>
                <Badge bg="secondary" className="ms-2">{resources.length} resources</Badge>
              </Card.Header>
              <Card.Body>
                {resources.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    <strong>No resources available.</strong>
                    <p className="mb-0 mt-2">
                      Resources for your assigned programmes will appear here once uploaded.
                    </p>
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover size="sm">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Uploaded</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resources.map(resource => (
                          <tr key={resource.id}>
                            <td className="text-center" style={{ width: '40px' }}>
                              <span style={{ fontSize: '1.2rem' }}>
                                {getResourceIcon(resource.category, resource.type)}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong>{resource.title}</strong>
                                {resource.description && (
                                  <div className="small text-muted">{resource.description.substring(0, 100)}...</div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge bg="secondary">{resource.category}</Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {resource.uploadedAt ? resource.uploadedAt.toLocaleDateString() : 'N/A'}
                              </small>
                            </td>
                            <td>
                              {resource.isExternalLink ? (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open Link
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  href={resource.fileURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Sales Tab */}
          <Tab eventKey="sales" title="Sales">
            <Card>
              <Card.Header>
                <strong>Sales Dashboard</strong>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-3">
                  <strong>Sales Tracking:</strong> View sales performance and targets for your assigned programmes.
                </Alert>
                <Row>
                  <Col md={4} className="mb-3">
                    <Card className="bg-light">
                      <Card.Body className="text-center">
                        <h4 className="text-primary">-</h4>
                        <small className="text-muted">Total Sales</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Card className="bg-light">
                      <Card.Body className="text-center">
                        <h4 className="text-success">-</h4>
                        <small className="text-muted">This Month</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Card className="bg-light">
                      <Card.Body className="text-center">
                        <h4 className="text-info">-</h4>
                        <small className="text-muted">Target Progress</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <p className="text-muted">
                  Sales data will be displayed here once available. Contact the administrator
                  if you need access to sales tracking features.
                </p>
              </Card.Body>
            </Card>
          </Tab>

          {/* Reports Tab */}
          <Tab eventKey="reports" title="Reports">
            <Card>
              <Card.Header>
                <strong>Progress Reports</strong>
              </Card.Header>
              <Card.Body>
                <Alert variant="success" className="mb-3">
                  <strong>Report Access:</strong> You can generate and download progress reports
                  for learners in your assigned cohorts.
                </Alert>

                {learners.length > 0 ? (
                  <ProgressReportGenerator
                    learners={learners}
                    cohorts={userProfile?.assignedCohorts || []}
                    programs={assignedPrograms}
                    isExternal={true}
                    reportHistoryUserId={currentUser?.uid || null}
                  />
                ) : (
                  <Alert variant="warning">
                    No learners available for report generation.
                    Please ensure you have learners assigned to your cohorts.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <Card className="mt-4">
              <Card.Header>
                <strong>Daily Comments & Weekly Highs/Lows</strong>
              </Card.Header>
              <Card.Body>
                {commentError && (
                  <Alert variant="danger" onClose={() => setCommentError('')} dismissible>
                    {commentError}
                  </Alert>
                )}
                {commentSuccess && (
                  <Alert variant="success" onClose={() => setCommentSuccess('')} dismissible>
                    {commentSuccess}
                  </Alert>
                )}
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Select
                        value={commentType}
                        onChange={(e) => setCommentType(e.target.value)}
                      >
                        <option value="daily">Daily Comment</option>
                        <option value="weekly">Weekly Highs & Lows</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Cohort</Form.Label>
                      <Form.Select
                        value={commentCohortId}
                        onChange={(e) => setCommentCohortId(e.target.value)}
                      >
                        <option value="">Select cohort...</option>
                        {assignedCohorts.map((cohort) => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.name || cohort.id}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    {commentType === 'daily' ? (
                      <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={commentDate}
                          onChange={(e) => setCommentDate(e.target.value)}
                        />
                      </Form.Group>
                    ) : (
                      <Form.Group>
                        <Form.Label>Week Start</Form.Label>
                        <Form.Control
                          type="date"
                          value={weekStartDate}
                          onChange={(e) => setWeekStartDate(e.target.value)}
                        />
                      </Form.Group>
                    )}
                  </Col>
                </Row>

                {commentType === 'weekly' && (
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Week End</Form.Label>
                        <Form.Control
                          type="date"
                          value={weekEndDate}
                          onChange={(e) => setWeekEndDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {commentType === 'daily' ? (
                  <Form.Group className="mb-3">
                    <Form.Label>Daily Comment</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={dailyComment}
                      onChange={(e) => setDailyComment(e.target.value)}
                      placeholder="Write the daily update here..."
                    />
                  </Form.Group>
                ) : (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Highs</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={weeklyHighs}
                          onChange={(e) => setWeeklyHighs(e.target.value)}
                          placeholder="What went well this week?"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Lows</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={weeklyLows}
                          onChange={(e) => setWeeklyLows(e.target.value)}
                          placeholder="What needs improvement?"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Button
                  variant="primary"
                  onClick={handleSubmitComment}
                  disabled={commentSubmitting}
                >
                  {commentSubmitting ? 'Saving...' : 'Save Comment'}
                </Button>

                <div className="mt-4">
                  <h6 className="mb-2">Recent Comments</h6>
                  {commentHistoryLoading ? (
                    <div className="text-muted">Loading recent comments...</div>
                  ) : commentHistory.length === 0 ? (
                    <div className="text-muted">No comments submitted yet.</div>
                  ) : (
                    <Table responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Cohort</th>
                          <th>Date/Week</th>
                          <th>Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commentHistory.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <Badge bg={item.type === 'daily_comment' ? 'info' : 'secondary'}>
                                {item.type === 'daily_comment' ? 'Daily' : 'Weekly'}
                              </Badge>
                            </td>
                            <td>{item.cohortName || 'Unknown Cohort'}</td>
                            <td>
                              {item.type === 'daily_comment'
                                ? (item.date || 'N/A')
                                : `${item.weekStart || 'N/A'} → ${item.weekEnd || 'N/A'}`}
                            </td>
                            <td className="text-truncate" style={{ maxWidth: 280 }}>
                              {item.type === 'daily_comment'
                                ? item.dailyComment || ''
                                : `Highs: ${item.weeklyHighs || ''} | Lows: ${item.weeklyLows || ''}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
          </>
        )}
      </Container>
    </>
  );
};

export default ExternalFacilitatorDashboard;
